"use client";

import { formatTime, MS_PER_MINUTE, type SpecialTimes } from "@kedaat/engine";
import { CANDLE_MINUTE_CHOICES, type Locality } from "@kedaat/locations";
import type { ReactNode } from "react";
import { CandlesIcon, FastIcon, StarsIcon } from "./icons";

type IconComponent = (props: { className?: string }) => ReactNode;

interface Item {
  label: string;
  time: string;
  icon: IconComponent;
  note?: ReactNode;
}

export function SpecialTimesCard({
  special,
  locality,
  seconds,
  onCandleMinutesChange,
}: {
  special: SpecialTimes;
  locality: Locality;
  seconds: boolean;
  onCandleMinutesChange: (minutes: number) => void;
}) {
  const { candleLighting, shabbatEnd, fast } = special;
  const time = (ms: number, rounding: "floor" | "ceil") => formatTime(ms, rounding, { seconds });
  const items: Item[] = [];

  if (fast) {
    items.push({
      label: "תחילת הצום",
      icon: FastIcon,
      time: time(fast.start.ms, fast.start.rounding),
    });
  }

  if (candleLighting) {
    const custom = locality.candle;
    const alternate = custom.alternateMinutes;
    items.push({
      label: "הדלקת נרות",
      icon: CandlesIcon,
      time: time(candleLighting.ms, candleLighting.rounding),
      note: candleLighting.afterNightfall ? (
        "לאחר צאת הכוכבים, מאש קיימת"
      ) : (
        <>
          <label className="inline-flex items-center gap-1">
            <select
              aria-label="דקות לפני השקיעה"
              value={candleLighting.minutes}
              onChange={(e) => onCandleMinutesChange(Number(e.target.value))}
              className="cursor-pointer rounded border border-line bg-card px-1"
            >
              {CANDLE_MINUTE_CHOICES.map((m) => (
                <option key={m} value={m}>
                  {m}
                  {m === custom.minutes ? " (מנהג המקום)" : ""}
                </option>
              ))}
            </select>
            דק׳ לפני השקיעה
          </label>
          {custom.tier === "two_times" &&
            alternate !== undefined &&
            candleLighting.minutes === custom.minutes && (
              <span>
                {" "}
                · או {alternate} דק׳:{" "}
                <strong className="tabular-nums">
                  {time(
                    candleLighting.ms + (candleLighting.minutes - alternate) * MS_PER_MINUTE,
                    "floor",
                  )}
                </strong>
              </span>
            )}
          {custom.note && <span className="mt-1 block">{custom.note.he}</span>}
        </>
      ),
    });
  }

  if (fast && !shabbatEnd) {
    items.push({
      label: "סוף הצום",
      icon: FastIcon,
      time: time(fast.end.ms, fast.end.rounding),
    });
  }

  if (shabbatEnd) {
    items.push({
      label: "צאת שבת / חג",
      icon: StarsIcon,
      time: time(shabbatEnd.time.ms, shabbatEnd.time.rounding),
      note: `8.5° · לר״ת ${time(shabbatEnd.rabbeinuTam.ms, shabbatEnd.rabbeinuTam.rounding)}`,
    });
  }

  if (items.length === 0) return null;

  return (
    <section
      aria-label="זמני שבת, חג וצום"
      className="grid gap-px overflow-hidden rounded-2xl border border-accent/30 bg-accent/20 shadow-sm sm:grid-cols-2"
    >
      {items.map((item) => (
        <div key={item.label} className="bg-accent-soft px-4 py-3">
          <div className="flex items-baseline justify-between gap-3">
            <span className="flex items-center gap-2 font-medium">
              <item.icon className="size-5 shrink-0 self-center text-accent" />
              {item.label}
            </span>
            <span className="text-3xl font-bold tabular-nums text-accent">{item.time}</span>
          </div>
          {item.note && <div className="mt-1 text-xs leading-relaxed text-muted">{item.note}</div>}
        </div>
      ))}
    </section>
  );
}
